import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  ConfirmDialog,
  Input,
  PageHeader,
  RowMenu,
  Select,
  type SelectOption,
} from '../../components';
import { ApiError, apiRequest } from '../../lib/api';
import type { ListResponse } from '../../lib/useCrud';
import { LeadFormModal } from './LeadFormModal';
import type {
  LeadCreatePayload,
  LeadRow,
  LeadUpdatePayload,
  LookupOption,
  UserOption,
} from './types';

type LookupResponse<T> = ListResponse<T>;

const SORT_OPTIONS: SelectOption[] = [
  { value: 'created_at.desc', label: 'Newest first' },
  { value: 'created_at.asc', label: 'Oldest first' },
  { value: 'updated_at.desc', label: 'Recently updated' },
  { value: 'full_name.asc', label: 'Name A–Z' },
  { value: 'full_name.desc', label: 'Name Z–A' },
  { value: 'entry_date.desc', label: 'Entry date (newest)' },
];

const CONVERSION_OPTIONS: SelectOption[] = [
  { value: '', label: 'All leads' },
  { value: 'false', label: 'Not yet converted' },
  { value: 'true', label: 'Already converted' },
];

const PAGE_SIZE = 25;

export function LeadsListPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();

  const [q, setQ] = useState('');
  const [statusId, setStatusId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [sourceId, setSourceId] = useState('');
  const [salesId, setSalesId] = useState('');
  const [isConverted, setIsConverted] = useState('');
  const [sort, setSort] = useState('created_at.desc');
  const [offset, setOffset] = useState(0);

  const [editing, setEditing] = useState<LeadRow | 'new' | null>(null);
  const [deleting, setDeleting] = useState<LeadRow | null>(null);

  const listQuery = useMemo(() => {
    const [sortField, dir] = sort.split('.');
    const params = new URLSearchParams();
    params.set('limit', String(PAGE_SIZE));
    params.set('offset', String(offset));
    params.set('sort', sortField);
    params.set('dir', dir);
    if (q.trim()) params.set('q', q.trim());
    if (statusId) params.set('lead_status_id', statusId);
    if (categoryId) params.set('lead_category_id', categoryId);
    if (sourceId) params.set('source_id', sourceId);
    if (salesId) params.set('assigned_sales_user_id', salesId);
    if (isConverted) params.set('is_converted', isConverted);
    return params.toString();
  }, [q, statusId, categoryId, sourceId, salesId, isConverted, sort, offset]);

  const leads = useQuery({
    queryKey: ['leads', listQuery],
    queryFn: () => apiRequest<ListResponse<LeadRow>>(`/leads?${listQuery}`),
  });

  const statusesQ = useQuery({
    queryKey: ['lookup', 'lead-statuses'],
    queryFn: () =>
      apiRequest<LookupResponse<LookupOption>>(
        '/settings/lead-statuses?limit=100&include_inactive=false',
      ),
  });
  const categoriesQ = useQuery({
    queryKey: ['lookup', 'lead-categories'],
    queryFn: () =>
      apiRequest<LookupResponse<LookupOption>>(
        '/settings/lead-categories?limit=100&include_inactive=false',
      ),
  });
  const sourcesQ = useQuery({
    queryKey: ['lookup', 'sources'],
    queryFn: () =>
      apiRequest<LookupResponse<LookupOption>>(
        '/settings/sources?limit=100&include_inactive=false',
      ),
  });
  const usersQ = useQuery({
    queryKey: ['lookup', 'users'],
    queryFn: () =>
      apiRequest<LookupResponse<UserOption>>('/users?limit=200&include_inactive=false'),
  });

  const statuses = statusesQ.data?.data ?? [];
  const categories = categoriesQ.data?.data ?? [];
  const sources = sourcesQ.data?.data ?? [];
  const users = usersQ.data?.data ?? [];

  const statusById = new Map(statuses.map((s) => [s.id, s]));
  const userById = new Map(users.map((u) => [u.id, u]));

  const invalidate = () => qc.invalidateQueries({ queryKey: ['leads'] });

  const createMut = useMutation({
    mutationFn: (body: LeadCreatePayload) =>
      apiRequest<{ data: LeadRow }>('/leads', { method: 'POST', body }),
    onSuccess: invalidate,
  });
  const updateMut = useMutation({
    mutationFn: (args: { id: string; body: LeadUpdatePayload }) =>
      apiRequest<{ data: LeadRow }>(`/leads/${args.id}`, { method: 'PATCH', body: args.body }),
    onSuccess: invalidate,
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => apiRequest<void>(`/leads/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });

  const statusFilterOpts: SelectOption[] = statuses.map((s) => ({ value: s.id, label: s.name }));
  const categoryFilterOpts: SelectOption[] = categories.map((c) => ({ value: c.id, label: c.name }));
  const sourceFilterOpts: SelectOption[] = sources.map((s) => ({ value: s.id, label: s.name }));
  const userFilterOpts: SelectOption[] = users.map((u) => ({ value: u.id, label: u.full_name }));

  const total = leads.data?.pagination?.total ?? 0;
  const currentPageEnd = Math.min(offset + PAGE_SIZE, total);

  return (
    <>
      <PageHeader
        title="Leads"
        subtitle="Pipeline of prospects and existing conversations."
        actions={
          <Button variant="primary" icon="plus" onClick={() => setEditing('new')}>
            New lead
          </Button>
        }
      />

      <div className="card" style={{ marginBottom: 12 }}>
        <div
          className="card-body"
          style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(4, 1fr)' }}
        >
          <Input
            icon="search"
            placeholder="Search name, email, phone, company"
            value={q}
            onChange={(v) => {
              setQ(v);
              setOffset(0);
            }}
          />
          <Select
            placeholder="All statuses"
            value={statusId}
            options={statusFilterOpts}
            onChange={(v) => {
              setStatusId(v);
              setOffset(0);
            }}
          />
          <Select
            placeholder="All categories"
            value={categoryId}
            options={categoryFilterOpts}
            onChange={(v) => {
              setCategoryId(v);
              setOffset(0);
            }}
          />
          <Select
            placeholder="All sources"
            value={sourceId}
            options={sourceFilterOpts}
            onChange={(v) => {
              setSourceId(v);
              setOffset(0);
            }}
          />
          <Select
            placeholder="Any assignee"
            value={salesId}
            options={userFilterOpts}
            onChange={(v) => {
              setSalesId(v);
              setOffset(0);
            }}
          />
          <Select
            value={isConverted}
            options={CONVERSION_OPTIONS}
            onChange={(v) => {
              setIsConverted(v);
              setOffset(0);
            }}
          />
          <Select
            value={sort}
            options={SORT_OPTIONS}
            onChange={(v) => setSort(v)}
          />
        </div>
      </div>

      {leads.isLoading && <div className="card card-body muted">Loading…</div>}
      {leads.error && (
        <div className="alert">
          {leads.error instanceof Error ? leads.error.message : 'Failed to load leads'}
        </div>
      )}
      {leads.data && (
        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Company</th>
                <th>Status</th>
                <th>Sales</th>
                <th>Entry</th>
                <th>Converted</th>
                <th style={{ width: 60 }} />
              </tr>
            </thead>
            <tbody>
              {leads.data.data.length === 0 && (
                <tr>
                  <td colSpan={7} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                    No leads match this filter.
                  </td>
                </tr>
              )}
              {leads.data.data.map((row) => {
                const status = row.lead_status_id ? statusById.get(row.lead_status_id) : null;
                const assignee = row.assigned_sales_user_id
                  ? userById.get(row.assigned_sales_user_id)
                  : null;
                return (
                  <tr key={row.id}>
                    <td>
                      <Link to={`/leads/${row.id}`}>{row.full_name ?? 'Unnamed'}</Link>
                      {row.email && (
                        <div className="muted" style={{ fontSize: 11 }}>
                          {row.email}
                        </div>
                      )}
                    </td>
                    <td>{row.company ?? <span className="muted">—</span>}</td>
                    <td>
                      {status ? (
                        <span
                          className="badge"
                          style={{
                            background: (status.color ?? '#888') + '22',
                            color: status.color ?? 'var(--text)',
                          }}
                        >
                          {status.name}
                        </span>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td>{assignee?.full_name ?? <span className="muted">—</span>}</td>
                    <td>{row.entry_date ?? <span className="muted">—</span>}</td>
                    <td>
                      {row.is_converted ? (
                        row.converted_client_id ? (
                          <Link
                            to={`/clients/${row.converted_client_id}`}
                            className="badge green"
                            title="Open the linked client record"
                            style={{ textDecoration: 'none' }}
                          >
                            <span className="badge-dot" />
                            Converted
                          </Link>
                        ) : (
                          <span className="badge warn" title="Marked converted but no client is linked">
                            <span className="badge-dot" />
                            Converted
                          </span>
                        )
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td>
                      <RowMenu
                        items={[
                          {
                            icon: 'arrowRight',
                            label: 'Open',
                            onClick: () => navigate(`/leads/${row.id}`),
                          },
                          { icon: 'edit', label: 'Edit', onClick: () => setEditing(row) },
                          'divider',
                          {
                            icon: 'trash',
                            label: 'Delete',
                            onClick: () => setDeleting(row),
                            danger: true,
                          },
                        ]}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div
            className="card-header"
            style={{
              justifyContent: 'space-between',
              borderTop: '1px solid var(--border)',
              borderBottom: 'none',
            }}
          >
            <span className="muted" style={{ fontSize: 12 }}>
              {total === 0 ? '0' : `${offset + 1}–${currentPageEnd}`} of {total}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                Previous
              </Button>
              <Button
                disabled={currentPageEnd >= total}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <LeadFormModal
          row={editing === 'new' ? undefined : editing}
          statuses={statuses}
          categories={categories}
          sources={sources}
          users={users}
          onClose={() => setEditing(null)}
          onSubmit={async (body) => {
            if (editing === 'new') {
              await createMut.mutateAsync(body as LeadCreatePayload);
            } else {
              await updateMut.mutateAsync({ id: editing.id, body });
            }
            setEditing(null);
          }}
        />
      )}

      <ConfirmDialog
        open={Boolean(deleting)}
        title="Delete lead"
        message={
          deleting ? (
            <>
              Delete <strong>{deleting.full_name ?? 'this lead'}</strong>? This cannot be undone.
            </>
          ) : null
        }
        destructive
        confirmLabel="Delete"
        loading={deleteMut.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting) return;
          try {
            await deleteMut.mutateAsync(deleting.id);
            setDeleting(null);
          } catch (err) {
            alert(err instanceof ApiError ? err.message : 'Delete failed');
          }
        }}
      />
    </>
  );
}
